/**
 * Comprehension Q&A flow with adaptive feedback.
 *
 * Presents questions one at a time with large tap targets. Submissions go
 * through the offline outbox so attempts made on a flight or in a Wi-Fi-less
 * classroom queue and replay later. When online, the graded result drives
 * encouragement-mode messaging; a "harder book" button lets a confident reader
 * nudge the difficulty engine up on their next book.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import { ImagePlaceholder } from "@/features/book/ImagePlaceholder";
import { ReadAloud } from "@/features/book/ReadAloud";
import type { StreamingPage } from "@/features/book/useBookStream";
import type { AttemptResult, Question } from "@/lib/api";
import { submitAttempt } from "@/offline/outbox";

interface QuizFlowProps {
  childId: string;
  bookId: string;
  questions: Question[];
  /**
   * The book's pages (text + illustration), so the child can look back at the
   * story while answering. Already in the reader's state when the quiz starts,
   * so this issues no new network request.
   */
  pages?: StreamingPage[];
  /** The child's reading band before the quiz, to detect a level change. */
  startingLevel?: string;
  /** Invoked when the child asks for a harder book next time. */
  onRequestHarderBook?: () => void;
  /** Invoked when the quiz is finished. */
  onComplete?: (results: AttemptResult[]) => void;
}

type Phase = "answering" | "feedback" | "done";

export function QuizFlow({
  childId,
  bookId,
  questions,
  pages = [],
  startingLevel,
  onRequestHarderBook,
  onComplete,
}: QuizFlowProps) {
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState("");
  const [phase, setPhase] = useState<Phase>("answering");
  const [lastResult, setLastResult] = useState<AttemptResult | null>(null);
  const [queuedOffline, setQueuedOffline] = useState(false);
  const [results, setResults] = useState<AttemptResult[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const question = questions[index];

  // A question is only answerable as multiple choice when the model actually
  // returned options. If a band asked for MC but the model emitted no choices,
  // we fall back to the free-text box so the child can still answer and advance
  // instead of facing an empty, dead-ended question.
  const showChoices =
    question?.kind === "multiple_choice" && (question?.choices?.length ?? 0) > 0;

  // The page that this question is about. `pages.length` is the page count
  // because `totalPages` stays null while a book is still streaming.
  const questionPageIndex = useMemo(() => {
    const found = pages.findIndex((p) => p.pageNumber === question?.pageNumber);
    return found >= 0 ? found : 0;
  }, [pages, question?.pageNumber]);

  // Look-back panel: a kid can reopen the book without leaving the quiz. It
  // starts collapsed (DD-01) so it never pushes the question's aria-live
  // regions off-screen, and defaults to the current question's page.
  const [lookBackOpen, setLookBackOpen] = useState(false);
  const [lookBackIndex, setLookBackIndex] = useState(questionPageIndex);
  const lookBackPanelRef = useRef<HTMLDivElement>(null);

  // When the question changes, snap the look-back panel back to its page.
  useEffect(() => {
    setLookBackIndex(questionPageIndex);
  }, [questionPageIndex]);

  // Move focus into the panel when it opens so a child using the keyboard or a
  // screen reader lands on the book pages they just asked to see.
  useEffect(() => {
    if (lookBackOpen) lookBackPanelRef.current?.focus();
  }, [lookBackOpen]);

  if (!question) {
    return (
      <p role="status" style={{ fontSize: "1.25rem" }}>
        No questions for this book yet.
      </p>
    );
  }

  async function submit(value: string) {
    if (!value.trim() || submitting || !question) return;
    setSubmitting(true);
    const outcome = await submitAttempt({
      childId,
      bookId,
      questionId: question.id,
      answer: value.trim(),
      clientTimestamp: new Date().toISOString(),
    });
    setSubmitting(false);

    if (outcome.queued) {
      setQueuedOffline(true);
      setPhase("feedback");
      return;
    }
    if (outcome.result) {
      setLastResult(outcome.result);
      setResults((prev) => [...prev, outcome.result!]);
    }
    setPhase("feedback");
  }

  function next() {
    setAnswer("");
    setLastResult(null);
    setQueuedOffline(false);
    if (index < questions.length - 1) {
      setIndex((i) => i + 1);
      setPhase("answering");
    } else {
      setPhase("done");
    }
  }

  if (phase === "done") {
    const latestLevel = results.length
      ? results[results.length - 1]!.readingLevel
      : undefined;
    const currentLevel = latestLevel ?? startingLevel;
    const correctCount = results.filter((r) => r.correct).length;
    const gradedCount = results.length;
    const leveledUp =
      !!startingLevel && !!latestLevel && latestLevel !== startingLevel;
    return (
      <section aria-label="Quiz finished" className="kid-card" style={{ padding: "1.5rem" }}>
        <h2 style={{ fontSize: "1.75rem" }}>🎉 Great reading!</h2>
        <p style={{ fontSize: "1.25rem" }}>
          You finished all the questions for this book.
        </p>
        {gradedCount > 0 && (
          <p
            role="status"
            aria-live="polite"
            style={{ fontSize: "1.4rem", fontWeight: 700 }}
          >
            ⭐ You got {correctCount} out of {gradedCount} right!
          </p>
        )}
        {currentLevel && (
          <p style={{ fontSize: "1.25rem" }}>
            Your reading level is <strong>{currentLevel}</strong>.
          </p>
        )}
        {leveledUp && (
          <p
            role="status"
            aria-live="polite"
            style={{ fontSize: "1.4rem", fontWeight: 700, color: "var(--color-grape)" }}
          >
            ⭐ You moved up to level {latestLevel}! Keep it up!
          </p>
        )}
        {onRequestHarderBook && (
          <button
            type="button"
            className="kid-button kid-button--primary"
            onClick={onRequestHarderBook}
          >
            💪 Give me a harder book
          </button>
        )}
        <button
          type="button"
          className="kid-button kid-button--secondary"
          style={{ marginTop: "1rem" }}
          onClick={() => onComplete?.(results)}
        >
          🏠 All done!
        </button>
      </section>
    );
  }

  return (
    <section aria-label="Reading questions">
      <p style={{ fontWeight: 700 }} aria-live="polite">
        Question {index + 1} of {questions.length}
      </p>
      <h2 style={{ fontFamily: "var(--font-reader)", fontSize: "1.6rem" }}>
        {question.prompt}
      </h2>

      {phase === "answering" && showChoices && (
        <div role="group" aria-label="Choose your answer" style={{ display: "grid", gap: "0.75rem" }}>
          {question.choices.map((choice) => (
            <button
              key={choice}
              type="button"
              className="kid-button kid-button--secondary"
              style={{ justifyContent: "flex-start", width: "100%", fontSize: "1.25rem" }}
              disabled={submitting}
              onClick={() => {
                setAnswer(choice);
                void submit(choice);
              }}
            >
              {choice}
            </button>
          ))}
          {submitting && (
            <p role="status" aria-live="polite" style={{ fontSize: "1.1rem" }}>
              Checking your answer… ⏳
            </p>
          )}
        </div>
      )}

      {phase === "answering" && !showChoices && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit(answer);
          }}
        >
          <label htmlFor="answer" style={{ display: "block", fontSize: "1.25rem" }}>
            Type your answer
          </label>
          <input
            id="answer"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            style={{
              width: "100%",
              fontSize: "1.5rem",
              padding: "0.75rem 1rem",
              borderRadius: "var(--radius-kid)",
              border: "3px solid var(--color-grape)",
              marginBottom: "1rem",
              boxSizing: "border-box",
            }}
          />
          <button
            type="submit"
            className="kid-button kid-button--primary"
            disabled={submitting || !answer.trim()}
          >
            {submitting ? "Checking… ⏳" : "Check my answer"}
          </button>
          {submitting && (
            <p role="status" aria-live="polite" style={{ fontSize: "1.1rem", marginTop: "0.75rem" }}>
              Checking your answer…
            </p>
          )}
        </form>
      )}

      {phase === "feedback" && (
        <div role="status" aria-live="assertive" className="kid-card" style={{ padding: "1.25rem", marginTop: "1rem" }}>
          {queuedOffline ? (
            <p style={{ fontSize: "1.25rem" }}>
              📦 Saved! We'll check your answer when you're back online.
            </p>
          ) : (
            <>
              <p style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                {lastResult?.correct ? "✓ " : ""}
                {lastResult?.encouragement ?? "Nice try!"}
              </p>
              {lastResult?.feedback && (
                <p style={{ fontSize: "1.1rem" }}>{lastResult.feedback}</p>
              )}
            </>
          )}
          <button type="button" className="kid-button kid-button--primary" onClick={next}>
            {index < questions.length - 1 ? "Next question ▶" : "Finish 🎉"}
          </button>
        </div>
      )}

      {pages.length > 0 && (
        <div style={{ marginTop: "1.5rem" }}>
          <button
            type="button"
            className="kid-button kid-button--secondary"
            aria-expanded={lookBackOpen}
            aria-controls="lookback-panel"
            onClick={() => setLookBackOpen((open) => !open)}
          >
            📖 Look at the book {lookBackOpen ? "▲" : "▼"}
          </button>

          {lookBackOpen && pages[lookBackIndex] && (
            <div
              id="lookback-panel"
              ref={lookBackPanelRef}
              tabIndex={-1}
              role="group"
              aria-label="Look back at the book"
              className="kid-card"
              style={{
                padding: "1.25rem",
                marginTop: "1rem",
                display: "grid",
                gap: "1rem",
              }}
            >
              {/* Navigation stays pinned to the top of the panel so the Back /
                  Next buttons never shift position (and never require a scroll)
                  as pages with and without illustrations alternate. */}
              <nav
                aria-label="Look-back page navigation"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "1rem",
                  alignItems: "center",
                  position: "sticky",
                  top: "0.5rem",
                  zIndex: 1,
                  background: "var(--color-cloud)",
                  paddingBottom: "0.5rem",
                }}
              >
                <button
                  type="button"
                  className="kid-button kid-button--primary"
                  onClick={() => setLookBackIndex((i) => Math.max(0, i - 1))}
                  disabled={lookBackIndex === 0}
                >
                  ◀ Back
                </button>
                <span aria-live="polite" style={{ fontWeight: 700 }}>
                  Page {pages[lookBackIndex]!.pageNumber} of {pages.length}
                </span>
                <button
                  type="button"
                  className="kid-button kid-button--primary"
                  onClick={() =>
                    setLookBackIndex((i) => Math.min(pages.length - 1, i + 1))
                  }
                  disabled={lookBackIndex >= pages.length - 1}
                >
                  Next ▶
                </button>
              </nav>

              {lookBackIndex !== questionPageIndex && (
                <button
                  type="button"
                  className="kid-button kid-button--secondary"
                  onClick={() => setLookBackIndex(questionPageIndex)}
                >
                  ↩ Back to this question's page
                </button>
              )}

              <ImagePlaceholder
                aspectRatio={pages[lookBackIndex]!.aspectRatio}
                imageUrl={pages[lookBackIndex]!.image?.url ?? null}
                alt={`Illustration for page ${pages[lookBackIndex]!.pageNumber}`}
                expectsImage={false}
              />
              <p
                style={{
                  fontFamily: "var(--font-reader)",
                  fontSize: "1.4rem",
                  lineHeight: 1.7,
                  margin: 0,
                }}
              >
                {pages[lookBackIndex]!.text || "…"}
              </p>
              <ReadAloud text={pages[lookBackIndex]!.text} />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
